// src/pages/finance/invoices/InvoicesPage.tsx
//
// The clinic's invoice list and detail drawer (I8).
//
// ── What this replaced, and why wholesale ────────────────────────────────────────────────────
//
// The previous page offered Finalize / Pay / Void buttons that COULD NOT WORK, for two
// independent reasons:
//
//   1. all three were gated on `invoice.metadata?.stripe_invoice_id`. Finance I4 moved the Stripe
//      mirror from create time to ISSUE time and stopped writing that metadata key at create, so
//      the buttons never rendered on a new invoice; and
//   2. their request bodies all carried `companyId`, which the service's global ValidationPipe
//      (`forbidNonWhitelisted`) has rejected with a 400 since the company removal.
//
// It also fetched the clinic company on every list load purely to obtain that `companyId`, and
// captured a `searchTerm` it never sent anywhere.
//
// The replacement is the I4/I5 model: a draft is local and editable, ISSUE is the single
// transition out of draft (and the only thing that touches Stripe), and money is recorded through
// the manual payment ledger.
import React, { useMemo, useState } from "react";
import { Alert, Button, Modal, Space, Tag, Typography } from "antd";
import {
	CheckCircleOutlined,
	CloseOutlined,
	DollarOutlined,
	EditOutlined,
	LinkOutlined,
	PlusOutlined,
	SendOutlined,
	UndoOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

// Layout
import Layout from "../../../components/Layout";
import PageContainer from "../../../components/shared/PageContainer";
import {
	SectionCard,
	DataTable,
	PageToolbar,
	DetailDrawer,
	StatusTag,
	InlineLoading,
} from "../../../components/shared";

// API
import {
	cancelInvoice,
	fetchInvoice,
	fetchInvoicePayments,
	fetchInvoices,
	issueInvoice,
	sendInvoice,
	reverseInvoicePayment,
	fetchClinicSettings,
} from "../../../api/finance/FinanceApi";
import { financeKeys } from "../../../api/finance/financeQueryKeys";
import { fetchPatientsByIds } from "../../../api/healthcare/PatientApi";

// Types
import {
	ISSUE_WARNING_STOCK_SHORTFALL,
	ISSUE_WARNING_STRIPE_MIRROR_PENDING,
	Invoice,
	InvoicePayment,
	InvoiceStatus,
	IssueInvoiceResult,
	PAYMENT_METHOD_LABELS,
	PAYMENT_WARNING_STRIPE_SETTLEMENT_PENDING,
	PaymentStatus,
} from "../../../types/finance/FinanceTypes";

// Components
import { CreateInvoiceModal, InvoiceDocument, RecordPaymentModal } from "../../../components/finance/invoicing";

// Utils
import { formatCents, formatDollarString } from "../../../utils/finance/money";
import { financeErrorMessage, financeErrorStatus } from "../../../utils/finance/financeError";
import {
	canCancel,
	canRecordPayment,
	canSend,
	invoiceDisplayNumber,
	invoiceStatusPresentation,
	isDraft,
	stockShortfallLines,
} from "../../../utils/finance/invoiceDisplay";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";
import { useNotifications } from "../../../context/NotificationContext";
import { isUserUuid } from "../../../utils/auth/userUuid";

const { Text } = Typography;
const { confirm } = Modal;

const STATUS_FILTER_OPTIONS = [
	{ value: InvoiceStatus.DRAFT, label: "Draft" },
	{ value: InvoiceStatus.SENT, label: "Issued" },
	{ value: InvoiceStatus.PARTIALLY_PAID, label: "Part paid" },
	{ value: InvoiceStatus.PAID, label: "Paid" },
	// The filter maps onto the server's derived-overdue predicate, which also returns pre-I4 rows
	// whose STORED status is `overdue`. Both are "late" to an operator.
	{ value: InvoiceStatus.OVERDUE, label: "Overdue" },
	{ value: InvoiceStatus.CANCELLED, label: "Cancelled" },
];

const InvoicesPage: React.FC = () => {
	const perPage = loadUserPreferences().tableRowsPerPage;
	const queryClient = useQueryClient();
	const { showError, showSuccess, showWarning } = useNotifications();

	const [page, setPage] = useState<number>(1);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [statusFilter, setStatusFilter] = useState<string>("");

	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const [createOpen, setCreateOpen] = useState<boolean>(false);
	const [editing, setEditing] = useState<Invoice | null>(null);
	const [paymentOpen, setPaymentOpen] = useState<boolean>(false);

	const listParams = useMemo(
		() => ({
			page,
			per_page: perPage,
			...(searchTerm ? { search: searchTerm } : {}),
			...(statusFilter ? { status: statusFilter as InvoiceStatus } : {}),
		}),
		[page, perPage, searchTerm, statusFilter],
	);

	const { data: list, isLoading } = useQuery({
		queryKey: financeKeys.invoiceList(listParams),
		queryFn: () => fetchInvoices(listParams),
	});

	const invoices = list?.data ?? [];
	const total = list?.total ?? 0;

	const { data: settings } = useQuery({
		queryKey: financeKeys.clinicSettings(),
		queryFn: fetchClinicSettings,
	});

	// ── Patient labels for the visible page ───────────────────────────────────────────────────
	// An invoice carries only `userId`; the finance service holds no patient names by design.
	// One bulk lookup per page of rows, keyed on the ids so it re-runs when the page changes and
	// is served from cache when the operator pages back.
	const patientIds = useMemo(
		() =>
			Array.from(new Set(invoices.map((invoice) => String(invoice.userId)))).filter(isUserUuid),
		[invoices],
	);

	const { data: patients } = useQuery({
		queryKey: financeKeys.invoicePatients(patientIds),
		queryFn: () => fetchPatientsByIds(patientIds),
		enabled: patientIds.length > 0,
	});

	const patientNameById = useMemo(() => {
		const map = new Map<string, string>();
		(patients ?? []).forEach((patient) => {
			const name = `${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim();
			map.set(String(patient.id), name || patient.email || `Patient ${patient.id}`);
		});
		return map;
	}, [patients]);

	// ── Selected invoice + its ledger ─────────────────────────────────────────────────────────
	const {
		data: selectedInvoice,
		isLoading: invoiceLoading,
		isError: invoiceError,
		error: invoiceErrorObj,
	} = useQuery({
		queryKey: financeKeys.invoice(selectedId ?? ""),
		queryFn: () => fetchInvoice(selectedId as string),
		enabled: !!selectedId,
		// A 404 means the row is genuinely gone (cancelled and pruned, or the list is stale after
		// another operator's action). Retrying it three times just delays the message by a couple
		// of seconds and then still shows nothing.
		retry: (failureCount, error) => financeErrorStatus(error) !== 404 && failureCount < 2,
	});

	const { data: ledger, isLoading: ledgerLoading, isError: ledgerError } = useQuery({
		queryKey: financeKeys.invoicePayments(selectedId ?? ""),
		queryFn: () => fetchInvoicePayments(selectedId as string),
		enabled: !!selectedId && !!selectedInvoice && !isDraft(selectedInvoice),
		// A draft has no ledger, and the endpoint is newer than the rest of I5 — a clinic running
		// an older finance image 404s it. That must dim one card, never break the drawer.
		retry: false,
	});

	const refreshInvoice = (id: string) => {
		queryClient.invalidateQueries({ queryKey: financeKeys.invoices() });
		queryClient.invalidateQueries({ queryKey: financeKeys.invoice(id) });
		queryClient.invalidateQueries({ queryKey: financeKeys.invoicePayments(id) });
	};

	// ── The stock-shortfall announcement (S13) ────────────────────────────────────────────────
	//
	// ⚠️ **Insufficient stock WARNS, ALLOWS and goes NEGATIVE** (parent decision D2). By the time
	// this runs the invoice is issued, numbered and payable — so this disables no control, offers
	// no undo and gates nothing. It exists only because a decision to let a sale proceed against a
	// short shelf is only defensible if a human is actually told.
	//
	// TWO reads, deliberately, because they are not interchangeable:
	//   - the `stock_shortfall` WARNING says a shortfall happened on THIS request. Commerce answers
	//     a replayed delivery with an empty list and cannot reconstruct it, so a delivery whose
	//     response was lost raises no warning at all;
	//   - `metadata.stock_shortfalls` is the durable record finance wrote from the delivery that
	//     actually moved the stock, and is what carries the per-line detail.
	//
	// ⚠️ The detail is absent on exactly one path: `POST /invoices/:id/issue` for a NON-Stripe
	// invoice returns the entity as it was saved INSIDE the issue transaction, and the drain that
	// writes the metadata runs after that commit. (`POST /send` re-reads the invoice after issuing,
	// and the Stripe-mirror branches of `/issue` re-read it under the mirror lock, so both carry
	// it.) Rather than fire a second request from a toast handler, the message degrades to naming
	// the fact and pointing at the drawer — which re-fetches and shows every line.
	const announceStockShortfall = (result: IssueInvoiceResult) => {
		if (!result.warnings.includes(ISSUE_WARNING_STOCK_SHORTFALL)) {
			return;
		}

		const lines = stockShortfallLines(result.invoice);

		showWarning(
			"Invoice issued — stock shortfall",
			lines.length
				? `${lines.join(" ")} The invoice is issued and payable — stock was taken negative rather than the sale being blocked.`
				: "The invoice is issued and payable. One or more product lines were short on stock and were taken negative — open the invoice to see which.",
			// Persistent, dismissed by hand. A three-second toast for "you have just sold stock the
			// shelf did not have" is a warning nobody reads.
			{ duration: 0 },
		);
	};

	const issueMutation = useMutation({
		mutationFn: (id: string) => issueInvoice(id),
		onSuccess: (result) => {
			refreshInvoice(result.invoice.id);

			if (result.warnings.includes(ISSUE_WARNING_STRIPE_MIRROR_PENDING)) {
				// ⚠️ NOT an error. The invoice IS issued — numbered, snapshotted, immutable — and
				// only its Stripe payment page is missing, because the local issue has to commit
				// before the Stripe round trip. Re-issuing re-claims and rebuilds the mirror.
				showWarning(
					"Issued — payment link pending",
					"The invoice is issued. Its Stripe payment page has not been created yet; use Retry payment link to try again.",
				);
			} else {
				showSuccess("Invoice issued");
			}

			// Independent of the mirror warning — an invoice can be short on stock AND missing its
			// payment page, and each is a separate thing to do something about.
			announceStockShortfall(result);
		},
		onError: (error) => showError(financeErrorMessage(error, "Failed to issue invoice")),
	});

	// Sending is what a clinic means by "send an invoice": it issues the invoice (if still a draft)
	// AND emails it + schedules the overdue reminders. `issueMutation` above only issues + builds the
	// Stripe mirror and mails nothing — it is kept solely for the "Retry payment link" recovery.
	const sendMutation = useMutation({
		mutationFn: (id: string) => sendInvoice(id),
		onSuccess: (result) => {
			refreshInvoice(result.invoice.id);

			if (result.warnings.includes(ISSUE_WARNING_STRIPE_MIRROR_PENDING)) {
				// Sent successfully — the patient's email carries a working link; only the Stripe
				// hosted payment page is still being built. "Retry payment link" rebuilds it.
				showWarning(
					"Sent — payment link pending",
					"The invoice was sent. Its Stripe payment page has not been created yet; use Retry payment link to try again.",
				);
			} else {
				showSuccess("Invoice sent");
			}

			// The send path is where a shortfall actually shows up in practice: `POST /send` issues
			// the draft on the way past, so it is the request the fulfilment is delivered on.
			announceStockShortfall(result);
		},
		onError: (error) => showError(financeErrorMessage(error, "Failed to send invoice")),
	});

	const cancelMutation = useMutation({
		mutationFn: (id: string) => cancelInvoice(id),
		onSuccess: (invoice) => {
			refreshInvoice(invoice.id);
			showSuccess("Invoice cancelled");
		},
		onError: (error) =>
			// A 409 here means the row is final (paid / already cancelled / imported) and the list
			// is stale — the server's own wording says which, so it is surfaced as-is.
			showError(financeErrorMessage(error, "Failed to cancel invoice")),
	});

	const reverseMutation = useMutation({
		mutationFn: ({ invoiceId, paymentId }: { invoiceId: string; paymentId: string }) =>
			reverseInvoicePayment(invoiceId, paymentId),
		onSuccess: (result) => {
			refreshInvoice(result.invoice.id);

			// ⚠️ Reversing a manual payment on a STRIPE-collected invoice always leaves the two
			// systems disagreeing: Stripe was told the invoice was paid out of band and there is
			// no un-pay API. The local ledger is correct and committed; only a human can fix
			// Stripe's copy, so this must be said out loud rather than swallowed as a success.
			if (result.warnings.includes(PAYMENT_WARNING_STRIPE_SETTLEMENT_PENDING)) {
				showWarning(
					"Payment reversed — Stripe still shows it as paid",
					"Stripe has no way to un-pay an invoice marked paid out of band. Correct it in the Stripe dashboard.",
				);
			} else {
				showSuccess("Payment reversed");
			}
		},
		onError: (error) => showError(financeErrorMessage(error, "Failed to reverse payment")),
	});

	// The DURABLE half of the shortfall story, straight off the invoice the drawer just fetched —
	// so it is still there after the toast has been dismissed, after a reload, and (unlike the
	// warning) after a delivery whose response was lost.
	const shortfallLines = useMemo(
		() => stockShortfallLines(selectedInvoice),
		[selectedInvoice],
	);

	const openInvoice = (invoice: Invoice) => {
		setSelectedId(invoice.id);
		setDrawerOpen(true);
	};

	const confirmCancel = (invoice: Invoice) => {
		confirm({
			title: "Cancel invoice",
			content: `${invoiceDisplayNumber(invoice)} will be cancelled. This cannot be undone.`,
			okText: "Cancel invoice",
			okType: "danger",
			cancelText: "Keep it",
			onOk: () => cancelMutation.mutateAsync(invoice.id),
		});
	};

	const confirmReverse = (invoiceId: string, payment: InvoicePayment) => {
		confirm({
			title: "Reverse payment",
			content:
				"The payment stays on the ledger marked reversed and the balance goes back up. No money moves — use a refund for that.",
			okText: "Reverse",
			okType: "danger",
			onOk: () => reverseMutation.mutateAsync({ invoiceId, paymentId: payment.id }),
		});
	};

	// ── Columns ──────────────────────────────────────────────────────────────────────────────
	const columns: ColumnsType<Invoice> = [
		{
			title: "Invoice #",
			key: "invoiceNumber",
			width: 150,
			// ⚠️ NOT `dataIndex: "invoiceNumber"` — it is NULL on a draft, which would render an
			// empty cell that reads as a loading bug.
			render: (_, invoice) => <Text strong>{invoiceDisplayNumber(invoice)}</Text>,
		},
		{
			title: "Patient",
			key: "patient",
			width: 190,
			render: (_, invoice) =>
				patientNameById.get(String(invoice.userId)) ?? (
					<Text type="secondary">Patient {invoice.userId}</Text>
				),
		},
		{
			title: "Issue date",
			dataIndex: "issueDate",
			key: "issueDate",
			width: 120,
			render: (date: string) => (date ? format(new Date(date), "dd MMM yyyy") : "—"),
		},
		{
			title: "Due date",
			dataIndex: "dueDate",
			key: "dueDate",
			width: 120,
			render: (date: string) => (date ? format(new Date(date), "dd MMM yyyy") : "—"),
		},
		{
			title: "Status",
			key: "status",
			width: 120,
			// Renders `effectiveStatus`, never `status`: lateness is DERIVED server-side against
			// the database's clock, and the stored status of a late invoice still reads `sent`.
			//
			// The colour and label are passed EXPLICITLY. `StatusTag`'s own map has no `sent` and
			// no `overdue` row, so relying on it would render both as an unstyled default tag
			// showing the raw enum value — and "overdue" is the one status on this screen that has
			// to be visually distinct.
			render: (_, invoice) => {
				const presentation = invoiceStatusPresentation(invoice.effectiveStatus ?? invoice.status);
				return (
					<span data-testid="invoices-status-tag">
						<StatusTag
							status={invoice.effectiveStatus ?? invoice.status}
							color={presentation.color}
							label={presentation.label}
						/>
					</span>
				);
			},
		},
		{
			title: "Total",
			key: "total",
			width: 120,
			align: "right",
			render: (_, invoice) => formatDollarString(invoice.total),
		},
		{
			title: "Paid",
			key: "paid",
			width: 110,
			align: "right",
			render: (_, invoice) => formatCents(invoice.amountPaidCents),
		},
		{
			title: "Balance",
			key: "balance",
			width: 120,
			align: "right",
			render: (_, invoice) => (
				<Text strong={Number(invoice.balanceDueCents ?? 0) > 0}>
					{formatCents(invoice.balanceDueCents)}
				</Text>
			),
		},
		{
			title: "",
			key: "actions",
			width: 210,
			render: (_, invoice) => (
				<Space size={4} onClick={(e) => e.stopPropagation()}>
					{isDraft(invoice) && (
						<Button
							type="text"
							size="small"
							icon={<EditOutlined />}
							onClick={() => {
								setEditing(invoice);
								setCreateOpen(true);
							}}
						>
							Edit
						</Button>
					)}
					{canSend(invoice) && (
						<Button
							type="link"
							size="small"
							icon={<SendOutlined />}
							loading={sendMutation.isPending}
							onClick={() => sendMutation.mutate(invoice.id)}
							data-testid="invoices-send-btn"
						>
							Send
						</Button>
					)}
					{invoice.stripeHostedInvoiceUrl && (
						<Button
							type="link"
							size="small"
							icon={<LinkOutlined />}
							href={invoice.stripeHostedInvoiceUrl}
							target="_blank"
							rel="noopener noreferrer"
						>
							Pay link
						</Button>
					)}
				</Space>
			),
		},
	];

	// ── Drawer header actions ────────────────────────────────────────────────────────────────
	const drawerActions = selectedInvoice ? (
		<Space size={8}>
			{isDraft(selectedInvoice) && (
				<Button
					icon={<EditOutlined />}
					onClick={() => {
						setEditing(selectedInvoice);
						setCreateOpen(true);
					}}
				>
					Edit
				</Button>
			)}
			{canSend(selectedInvoice) && (
				<Button
					type="primary"
					icon={<SendOutlined />}
					loading={sendMutation.isPending}
					onClick={() => sendMutation.mutate(selectedInvoice.id)}
				>
					{isDraft(selectedInvoice) ? "Send" : "Resend"}
				</Button>
			)}
			{/*
			  Re-issuing an ALREADY issued invoice is the documented recovery for a pending Stripe
			  mirror: the claim flag was released by the failure, so the call re-claims it and
			  rebuilds. It allocates no second number.
			*/}
			{!isDraft(selectedInvoice) && selectedInvoice.stripeSyncPending && (
				<Button
					icon={<UndoOutlined />}
					loading={issueMutation.isPending}
					onClick={() => issueMutation.mutate(selectedInvoice.id)}
				>
					Retry payment link
				</Button>
			)}
			{canRecordPayment(selectedInvoice) && (
				<Button
					type="primary"
					icon={<DollarOutlined />}
					onClick={() => setPaymentOpen(true)}
					data-testid="invoices-record-payment-btn"
				>
					Record payment
				</Button>
			)}
			{selectedInvoice.stripeHostedInvoiceUrl && (
				<Button
					icon={<LinkOutlined />}
					href={selectedInvoice.stripeHostedInvoiceUrl}
					target="_blank"
					rel="noopener noreferrer"
				>
					Open pay link
				</Button>
			)}
			{canCancel(selectedInvoice) && (
				<Button danger icon={<CloseOutlined />} onClick={() => confirmCancel(selectedInvoice)}>
					Cancel
				</Button>
			)}
		</Space>
	) : undefined;

	const ledgerColumns: ColumnsType<InvoicePayment> = [
		{
			title: "Received",
			key: "receivedAt",
			width: 130,
			render: (_, payment) =>
				payment.receivedAt ? format(new Date(payment.receivedAt), "dd MMM yyyy") : "—",
		},
		{
			title: "Method",
			key: "method",
			width: 140,
			render: (_, payment) => PAYMENT_METHOD_LABELS[payment.method] ?? payment.method,
		},
		{
			title: "Reference",
			dataIndex: "reference",
			key: "reference",
			render: (reference: string | null) => reference || "—",
		},
		{
			title: "Amount",
			key: "amount",
			width: 120,
			align: "right",
			render: (_, payment) => formatCents(payment.amountCents),
		},
		{
			title: "Status",
			key: "status",
			width: 110,
			render: (_, payment) =>
				payment.status === PaymentStatus.REVERSED ? (
					<Tag color="red">Reversed</Tag>
				) : (
					<Tag color="green">
						<CheckCircleOutlined /> Recorded
					</Tag>
				),
		},
		{
			title: "",
			key: "reverse",
			width: 110,
			render: (_, payment) =>
				payment.status === PaymentStatus.REVERSED || !selectedInvoice ? null : (
					<Button
						type="link"
						danger
						size="small"
						icon={<UndoOutlined />}
						onClick={() => confirmReverse(selectedInvoice.id, payment)}
					>
						Reverse
					</Button>
				),
		},
	];

	return (
		<Layout>
			<PageContainer data-testid="invoices-root">
				<PageToolbar
					primaryAction={{
						label: "Create Invoice",
						icon: <PlusOutlined />,
						onClick: () => {
							setEditing(null);
							setCreateOpen(true);
						},
						testId: "invoices-create-btn",
					}}
					filters={[
						{
							type: "search",
							placeholder: "Search invoice number…",
							value: searchTerm,
							onChange: (value) => {
								setSearchTerm(value);
								setPage(1);
							},
							width: 240,
							testId: "invoices-search",
						},
						{
							type: "select",
							placeholder: "Any status",
							options: STATUS_FILTER_OPTIONS,
							value: statusFilter || undefined,
							onChange: (value) => {
								setStatusFilter(value ?? "");
								setPage(1);
							},
							allowClear: true,
							width: 170,
						},
					]}
					onReset={() => {
						setSearchTerm("");
						setStatusFilter("");
						setPage(1);
					}}
				/>

				{/*
				  Search matches `invoiceNumber` only — deliberately not notes or line descriptions,
				  which are operator free text and may carry clinical detail. A draft has no number,
				  so it can never appear in a search result; saying so beats an operator concluding
				  their draft has been lost.
				*/}
				{searchTerm && (
					<Alert
						type="info"
						showIcon
						style={{ marginBottom: 12 }}
						message="Search matches the invoice number only — drafts are not numbered yet and will not appear."
					/>
				)}

				<DataTable
					columns={columns}
					dataSource={invoices}
					rowKey="id"
					loading={isLoading}
					total={total}
					currentPage={page}
					pageSize={perPage}
					onPageChange={(next) => setPage(next)}
					onRowClick={openInvoice}
					emptyTitle="No invoices found"
					emptyDescription="Create your first invoice to get started"
					emptyAction={{
						label: "Create Invoice",
						onClick: () => {
							setEditing(null);
							setCreateOpen(true);
						},
					}}
				/>

				<DetailDrawer
					open={drawerOpen}
					onClose={() => {
						setDrawerOpen(false);
						setSelectedId(null);
					}}
					width="70vw"
					title={selectedInvoice ? invoiceDisplayNumber(selectedInvoice) : "Invoice"}
					subtitle={
						selectedInvoice?.collectViaStripe ? "Collected via Stripe" : "Collected by the clinic"
					}
					headerActions={drawerActions}
				>
					{/*
					  An empty drawer is indistinguishable from a slow one. A failed load says so,
					  and a 404 says the specific thing an operator can act on: the row is gone and
					  the list they clicked from is stale.
					*/}
					{invoiceError && (
						<Alert
							type="error"
							showIcon
							data-testid="invoices-drawer-error"
							message={
								financeErrorStatus(invoiceErrorObj) === 404
									? "This invoice no longer exists"
									: "Could not load this invoice"
							}
							description={
								financeErrorStatus(invoiceErrorObj) === 404
									? "It has been removed since the list was loaded. Close the drawer and refresh."
									: financeErrorMessage(invoiceErrorObj, "Try again in a moment.")
							}
							action={
								<Button
									size="small"
									onClick={() =>
										queryClient.invalidateQueries({
											queryKey: financeKeys.invoice(selectedId as string),
										})
									}
								>
									Retry
								</Button>
							}
						/>
					)}

					{invoiceLoading && !invoiceError && <InlineLoading />}

					{selectedInvoice && !invoiceError && (
						<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
							{selectedInvoice.stripeSyncPending && (
								<Alert
									type="info"
									showIcon
									message="Issued — payment link pending"
									description="This invoice is issued and final. Its Stripe payment page has not been created yet; use Retry payment link above."
								/>
							)}

							{/*
							  ⚠️ Internal only (parent decision A4). This is the STAFF drawer; nothing
							  about stock reaches `InvoiceDocument`, which is the component the
							  unauthenticated public pay page mounts.
							*/}
							{shortfallLines.length > 0 && (
								<Alert
									type="warning"
									showIcon
									data-testid="invoices-stock-shortfall"
									message="Issued with a stock shortfall"
									description={
										<>
											{shortfallLines.map((line, index) => (
												<div key={index}>{line}</div>
											))}
											<div style={{ marginTop: 4 }}>
												The invoice is issued and payable — stock was taken negative rather
												than the sale being blocked. Correct the count in Inventory.
											</div>
										</>
									}
								/>
							)}

							{selectedInvoice.source && (
								<Alert
									type="warning"
									showIcon
									message={`Imported from ${selectedInvoice.source}`}
									description="An imported invoice is a record of a document issued elsewhere and cannot be changed here."
								/>
							)}

							<SectionCard title="Document">
								<InvoiceDocument
									invoice={selectedInvoice}
									settings={settings}
									patientName={patientNameById.get(String(selectedInvoice.userId))}
									// Staff drawer — opts IN to the internal notes. The public pay
									// page (I9) mounts this same component and must not pass it.
									showInternalNotes
								/>
							</SectionCard>

							{!isDraft(selectedInvoice) && (
								<SectionCard title="Payments" noPadding>
									{ledgerError ? (
										<div style={{ padding: 16 }}>
											<Text type="secondary">
												The payment ledger is unavailable on this clinic's finance service.
											</Text>
										</div>
									) : (
										<DataTable
											bare
											columns={ledgerColumns}
											dataSource={ledger ?? []}
											rowKey="id"
											loading={ledgerLoading}
											pagination={false}
											emptyTitle="No payments recorded"
										/>
									)}
								</SectionCard>
							)}
						</div>
					)}
				</DetailDrawer>

				<CreateInvoiceModal
					open={createOpen}
					invoice={editing}
					settings={settings}
					onClose={() => {
						setCreateOpen(false);
						setEditing(null);
					}}
					onSaved={(saved) => {
						if (selectedId === saved.id) {
							queryClient.invalidateQueries({ queryKey: financeKeys.invoice(saved.id) });
						}
					}}
				/>

				<RecordPaymentModal
					open={paymentOpen}
					invoice={selectedInvoice ?? null}
					onClose={() => setPaymentOpen(false)}
				/>
			</PageContainer>
		</Layout>
	);
};

export default InvoicesPage;
